import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldComponent } from '../../../types/FieldComponent';
import { TimeFilter } from '../filters/TimeFilter';
import Box from "@mui/material/Box";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Stack,
  TextField,
} from '@mui/material';
import api from "../../../services/api";
import {debounce} from "lodash";

type Role = {
  id: number | string;
  name: string; // pl. "Admin"
};

type User = {
  id: number | string;
  name: string;        // "Albert Bence"
  email?: string;
  roles: Role[];       // <-- több role
};


export const CustomFieldForAssignedPersons: FieldComponent<any, any> = ({
  onChange,
  isActive,
  value,
  defaultValue,
  allValue,
  hasTooltip,
  disabled,
  column,
}) => {

  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(() => {
    setLoading(true);

    const searchParams = new URLSearchParams();
    searchParams.set("inHouseRoles", "true");

    // Ha nálad api.get másképp várja a params-ot, akkor igazítsd hozzá
    api
      .get(`/users`, searchParams)
      .then((res: any) => setUsers(res))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ----- helpers -----
  const normalizeRoleName = (name: string) => name?.trim();
  const formatRoleLabel = (roleName: string) =>
    roleName
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const userHasRole = (u: User, roleName: string) =>
    (u.roles || []).some((r) => normalizeRoleName(r.name) === roleName);

  const mergeUsers = (base: User[], toAdd: User[]) => {
    const map = new Map<string, User>();
    base.forEach((u) => map.set(String(u.id), u));
    toAdd.forEach((u) => map.set(String(u.id), u));
    return Array.from(map.values());
  };

  // ----- dynamic role buttons (unique roles from all users) -----
  const roleNames = useMemo(() => {
    const set = new Set<string>();
    users.forEach((u) => {
      (u.roles || []).forEach((r) => {
        const rn = normalizeRoleName(r.name);
        if (rn) set.add(rn);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [users]);

  // ----- quick group add: add all users who have role -----
  const addRoleUsers = (roleName: string) => {
    const roleUsers = users.filter((u) => userHasRole(u, roleName));
    setSelectedUsers((prev) => mergeUsers(prev, roleUsers));
  };

  // optional: disable chip if all users with that role are already selected
  const isRoleFullySelected = useMemo(() => {
    const selectedSet = new Set(selectedUsers.map((u) => String(u.id)));
    return (roleName: string) => {
      const roleUsers = users.filter((u) => userHasRole(u, roleName));
      if (roleUsers.length === 0) return true;
      return roleUsers.every((u) => selectedSet.has(String(u.id)));
    };
  }, [users, selectedUsers]);

  // ----- grouping in autocomplete -----
  // Since users can have multiple roles, we choose a "primary" group:
  const getPrimaryRoleName = (u: User) => normalizeRoleName(u.roles?.[0]?.name) || "No role";

  const debouncedChange = useCallback(
    debounce((newValue) => {
      onChange(newValue);
    }, 200),
    []
  );

  useEffect(() => {
    debouncedChange(selectedUsers);
  }, [selectedUsers, debouncedChange]);

  return (
   <>
     <Box sx={{
       padding: '5px',
     }}>
       {/* Quick role buttons */}
       <Stack
         sx={{
           pb: '10px'
         }}
         direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
         {roleNames.map((roleName) => (
           <Chip
             size='small'
             key={roleName}
             label={`+ ${formatRoleLabel(roleName)}`}
             onClick={() => addRoleUsers(roleName)}
             disabled={isRoleFullySelected(roleName)}
             variant="outlined"
           />
         ))}
       </Stack>

       <Autocomplete
         sx={{
           '& .MuiNotchedOutlined-root .MuiOutlinedInput-notchedOutline':{
             borderWidth: '1px'
           }
         }}
         multiple
         loading={loading}
         options={users}
         value={selectedUsers}
         onChange={(_, value) => setSelectedUsers(value)}
         getOptionLabel={(option) => option.name}
         isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
         filterSelectedOptions
         groupBy={(option) => formatRoleLabel(getPrimaryRoleName(option))}
         renderInput={(params) => (
           <TextField
             {...params}
             size='small'
             label="Assigned Persons"
             placeholder="Search..."
             InputProps={{
               ...params.InputProps,
               endAdornment: (
                 <>
                   {loading ? <CircularProgress size={18} /> : null}
                   {params.InputProps.endAdornment}
                 </>
               ),
             }}
           />
         )}
       />
     </Box>
   </>
  );
};

CustomFieldForAssignedPersons.includes = (query, value) => true;

CustomFieldForAssignedPersons.comparator = () => 0;

CustomFieldForAssignedPersons.FilterRenderer = TimeFilter;
